import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useSession } from '@/composables/auth'
import AppLayout from '../AppLayout.vue'
import LoginPage from '../views/LoginPage.vue'
import HomeView from '../views/HomeView.vue'
import SubscriptionView from '../views/SubscriptionView.vue'
import PurchasesView from '../views/PurchasesView.vue'
import ThirdAccountsView from '../views/ThirdAccountsView.vue'
import BuildingInformationsView from '../views/BuildingInformationsView.vue'
import FurnituresView from '../views/furnitures/FurnituresView.vue'
import ObjectsView from '../views/furnitures/ObjectsView.vue'
import GatewaysView from '../views/furnitures/GatewaysView.vue'
import NetworksView from '../views/furnitures/NetworksView.vue'
import TagsView from '../views/furnitures/TagsView.vue'
import ZonesView from '../views/ZonesView.vue'
import RgpdView from '../views/RgpdView.vue'
import ModelingView from '../views/ModelingView.vue'
import SharingView from '../views/SharingView.vue'
import TransfertView from '../views/TransfertView.vue'
import PlansView from '../views/PlansView.vue'
import UserProfile from '@/views/UserProfile.vue'
import ChangePasswordView from '@/views/login/ChangePasswordView.vue'
import ForgottenPasswordView from '@/views/login/ForgottenPasswordView.vue'
import LoginSSOView from '@/views/login/LoginSSOView.vue'
import LegalInformationView from '@/views/LegalInformationView.vue'
import CallbackView from '@/views/callbacks/CallbackView.vue'
import ValidateShareView from '@/views/callbacks/ValidateShareView.vue'
import ValidateUserMailView from '@/views/callbacks/ValidateUserMailView.vue'
import RevokeCompanyInvitationView from '@/views/callbacks/RevokeCompanyInvitationView.vue'
import ChangeEmailView from '@/views/callbacks/ChangeEmailView.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'login' }
  },
  {
    path: '/callbacks',
    component: CallbackView,
    children: [
      {
        path: '/share/:user_id/:hash_key/authorization/:authorization_id/:brand?',
        name: 'callback-share',
        component: ValidateShareView
      },
      {
        path: '/validate/:user_id/:hash_key/:brand?',
        name: 'callback-validate-mail',
        component: ValidateUserMailView
      },
      {
        path: '/revoke-company/:user_id/:hash_key',
        name: 'callback-revoke-company',
        component: RevokeCompanyInvitationView
      },
      {
        path: '/change/:user_id/:hash_key/:brand?',
        name: 'callback-change-mail',
        component: ChangeEmailView
      }
    ]
  },
  {
    path: '',
    redirect: { name: 'login' },
    component: LoginPage,
    children: [
      {
        path: '/login',
        name: 'login',
        component: LoginSSOView
      },
      {
        path: '/password/reset/:brand?',
        name: 'reset-password',
        component: ForgottenPasswordView
      },
      {
        path: '/password/change/:user_id/:hash_key/:user_brand?',
        name: 'change-password',
        component: ChangePasswordView
      }
    ]
  },
  {
    path: '/my-account',
    component: AppLayout,
    redirect: { name: 'home' },
    children: [
      {
        path: '/home',
        name: 'home',
        component: HomeView,
        meta: {
          label: 'top-bar-title.account'
        }
      },
      {
        path: '/subscription',
        name: 'subscription',
        component: SubscriptionView,
        meta: {
          label: 'top-bar-title.subscription'
        }
      },
      {
        path: '/plans',
        name: 'plans',
        component: PlansView,
        meta: {
          label: 'top-bar-title.plans'
        }
      },
      {
        path: '/purchases',
        name: 'purchases',
        component: PurchasesView,
        meta: {
          label: 'top-bar-title.purchases'
        }
      },
      {
        path: '/third-accounts',
        name: 'third-accounts',
        component: ThirdAccountsView,
        meta: {
          label: 'top-bar-title.third-accounts'
        }
      },
      {
        path: '/building-informations',
        name: 'building-informations',
        component: BuildingInformationsView,
        meta: {
          label: 'top-bar-title.building-informations'
        }
      },
      {
        path: '/furnitures',
        component: FurnituresView,
        redirect: { name: 'objects' },
        meta: {
          label: 'top-bar-title.furnitures'
        },
        children: [
          {
            path: 'objects',
            name: 'objects',
            component: ObjectsView
          },
          {
            path: 'gateways',
            name: 'gateways',
            component: GatewaysView
          },
          {
            path: 'networks',
            name: 'networks',
            component: NetworksView
          },
          {
            path: 'tags',
            name: 'tags',
            component: TagsView
          }
        ]
      },
      {
        path: '/zones',
        name: 'zones',
        component: ZonesView,
        meta: {
          label: 'top-bar-title.zones'
        }
      },
      {
        path: '/rgpd',
        name: 'rgpd',
        component: RgpdView,
        meta: {
          label: 'top-bar-title.rgpd'
        }
      },
      {
        path: '/modeling',
        name: 'modeling',
        component: ModelingView,
        meta: {
          label: 'top-bar-title.modeling'
        }
      },
      {
        path: '/sharing',
        name: 'sharing',
        component: SharingView,
        meta: {
          label: 'top-bar-title.sharing'
        }
      },
      {
        path: '/transfert',
        name: 'transfert',
        component: TransfertView,
        meta: {
          label: 'top-bar-title.transfert'
        }
      },
      {
        path: '/user-profile',
        name: 'user-profile',
        component: UserProfile,
        meta: {
          label: 'top-bar-title.user-profile'
        }
      },
      {
        path: '/legal-informations',
        name: 'legal-informations',
        component: LegalInformationView,
        meta: {
          label: 'top-bar-title.legal-infos'
        }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

const { refreshHemisphereToken, ensureLoggedOrRedirect, loggedIn } = useSession()

router.beforeEach(async (to, from, next) => {
  const publicPages = ['login', 'reset-password', 'change-password']
  const publicPaths = ['callback']
  const authRequired = !publicPages.includes(to.name as string) && publicPaths.findIndex((p) => (to.name as string).startsWith(p)) == -1

  // trying to access a restricted page + not logged in
  // redirect to login page
  if (authRequired && !loggedIn.value) {
    await refreshHemisphereToken()
    if (loggedIn.value) {
      next()
    } else {
      await ensureLoggedOrRedirect(to.path)
    }
  } else {
    next()
  }
})

export default router
